import type { PortfolioDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	ClientPortfolioDomainEnum,
	PortfolioEventTypeEnum,
	ProductCategoryKeyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { getTravelInsuranceDuration } from "web_myplann/NotificationCenter/EventAlert/travelInsuranceDuration";
import {
	EventClassificationEnum,
	type EventVariant,
} from "web_myplann/NotificationCenter/EventAlert/types";

export const getEventVariant = (
	eventType: PortfolioEventTypeEnum,
): EventVariant =>
	eventType === PortfolioEventTypeEnum.Anniversary ? "info" : "warning";

const unambiguousClassification: Partial<
	Record<
		ClientPortfolioDomainEnum,
		Partial<Record<PortfolioEventTypeEnum, EventClassificationEnum>>
	>
> = {
	[ClientPortfolioDomainEnum.LifeInsurance]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.LifeInsuranceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.LifeInsuranceAnniversary,
	},
	[ClientPortfolioDomainEnum.PensionProducts]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.PensionEnd,
	},
	[ClientPortfolioDomainEnum.BuildingSavings]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.BuildingSavingsEnd,
	},
	[ClientPortfolioDomainEnum.Investments]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.InvestmentEnd,
	},
	[ClientPortfolioDomainEnum.BankingProducts]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.BankProductEnd,
	},
	[ClientPortfolioDomainEnum.Loans]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.LoanEnd,
		[PortfolioEventTypeEnum.FixationEnd]:
			EventClassificationEnum.ConsumerLoanFixationEnd,
	},
	[ClientPortfolioDomainEnum.BusinessLoans]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.BusinessLoanEnd,
		[PortfolioEventTypeEnum.FixationEnd]:
			EventClassificationEnum.BusinessLoanFixationEnd,
	},
	[ClientPortfolioDomainEnum.Commodities]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.CommodityEnd,
	},
	[ClientPortfolioDomainEnum.Services]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.ServiceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.ServiceAnniversary,
	},
	[ClientPortfolioDomainEnum.Leasing]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.LeasingEnd,
	},
	[ClientPortfolioDomainEnum.Cryptocurrencies]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.CryptocurrencyEnd,
	},
};

const productCategoryClassification: Partial<
	Record<
		ProductCategoryKeyEnum,
		Partial<Record<PortfolioEventTypeEnum, EventClassificationEnum>>
	>
> = {
	[ProductCategoryKeyEnum.VehicleInsurance]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.CarInsuranceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.CarInsuranceAnniversary,
	},
	[ProductCategoryKeyEnum.PropertyAndLiabilityInsurance]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.PropertyInsuranceEnd,
		[PortfolioEventTypeEnum.Anniversary]:
			EventClassificationEnum.PropertyInsuranceAnniversary,
	},
	[ProductCategoryKeyEnum.RealEstate]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.RealEstateEnd,
	},
	[ProductCategoryKeyEnum.HousingLoans]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.HousingLoanEnd,
		[PortfolioEventTypeEnum.FixationEnd]:
			EventClassificationEnum.HousingLoanFixationEnd,
	},
	[ProductCategoryKeyEnum.ConsumerLoans]: {
		[PortfolioEventTypeEnum.End]: EventClassificationEnum.ConsumerLoanEnd,
		[PortfolioEventTypeEnum.FixationEnd]:
			EventClassificationEnum.ConsumerLoanFixationEnd,
	},
};

const travelInsuranceClassification: Record<
	"short" | "long",
	EventClassificationEnum
> = {
	short: EventClassificationEnum.TravelInsuranceShortEnd,
	long: EventClassificationEnum.TravelInsuranceLongEnd,
};

type PortfolioClassificationInput = Pick<
	PortfolioDetailResponseDTO,
	"productCategory" | "startAt" | "endAt"
>;

export const resolveClassification = (
	domain: ClientPortfolioDomainEnum | null | undefined,
	eventType: PortfolioEventTypeEnum,
	portfolio?: PortfolioClassificationInput | null,
): EventClassificationEnum | null => {
	const productCategory = portfolio?.productCategory;
	const isAnniversary = eventType === PortfolioEventTypeEnum.Anniversary;

	if (productCategory === ProductCategoryKeyEnum.TravelInsurance) {
		if (isAnniversary) {
			return null;
		}
		const duration = getTravelInsuranceDuration(
			portfolio?.startAt,
			portfolio?.endAt,
		);
		return travelInsuranceClassification[duration];
	}

	const categoryClassification = productCategory
		? productCategoryClassification[productCategory]
		: undefined;
	if (categoryClassification) {
		return categoryClassification[eventType] ?? null;
	}

	if (domain === ClientPortfolioDomainEnum.PropertyInsurance) {
		return isAnniversary
			? EventClassificationEnum.AccidentHealthInsuranceAnniversary
			: EventClassificationEnum.AccidentHealthInsuranceEnd;
	}

	return domain
		? (unambiguousClassification[domain]?.[eventType] ?? null)
		: null;
};
